/**
 * Content of "Help & learning" (@digitalisation/enlixy-comp/onboarding): the
 * handbook, the guided tours and the learning path, in the user's language.
 *
 * The handbook keeps the texts of the former help page (i18n `helpPage.*`);
 * what the sheet adds is under `helpCenter.*`. Articles are plain text: the
 * KPI formulas (KaTeX on the old page) are written out, its screenshot
 * placeholders are gone.
 */

import type { TFunction } from "i18next";
import type { IconName } from "@digitalisation/enlixy-comp";
import type {
  HelpArticle,
  HelpArticleSection,
  Tour as BaseTour,
  TourStep as BaseStep,
} from "@digitalisation/enlixy-comp/onboarding";
import type { LearnEvent, LearnTask, TaskAction, TourId } from "./learning";

type T = TFunction;

const P = "helpPage";
const C = "helpCenter";

// ---------------------------------------------------------------- helpers

/** "1. Step" / "• Item" → "Step" / "Item": the old page put list markers into its texts. */
export const stripMarker = (text: string): string =>
  text.replace(/^\s*(?:[•·]|\d+\.)\s*/, "").trim();

/** "Term: explanation" → [term, explanation] when the term is short, else the text. */
export function listItem(text: string): string | [string, string] {
  const v = stripMarker(text);
  const m = /^([^:]{1,40}?)\s*:\s+(.+)$/.exec(v);
  return m ? [m[1], m[2]] : v;
}

/** An i18n array of the old page ([] when missing). */
function texts(t: T, key: string): string[] {
  const v: unknown = t(key, { returnObjects: true });
  return Array.isArray(v) ? v.map((x) => String(x)) : [];
}

const list = (t: T, key: string) => texts(t, key).map(listItem);

/** An intro that led into a list ("…:") as a sentence. */
const sentence = (text: string) => text.replace(/\s*:\s*$/, ".");
const heading = (text: string) => text.replace(/\s*:\s*$/, "");
/** "Quick tip: …" → "…": the handbook labels a tip itself. */
const tipText = (text: string) => text.replace(/^[^:]{1,25}:\s+/, "");

/** A colour dot or symbol in front of a list entry (the old page drew them). */
function marked(mark: string, item: string | [string, string]): string | [string, string] {
  return typeof item === "string" ? `${mark} ${item}` : [`${mark} ${item[0]}`, item[1]];
}

const STATUS: [string, string][] = [
  ["green", "🟢"],
  ["yellow", "🟡"],
  ["red", "🔴"],
  ["gray", "⚪"],
];
const SYMBOLS: [string, string][] = [
  ["refresh", "🔄"],
  ["warning", "⚠️"],
  ["chart", "📊"],
  ["connection", "🔌"],
];

/** The KPI tabs of a machine's Overview and of Compare Machines, as labelled there. */
const kpiTabs = (t: T) =>
  ["efficiency", "output", "downtime", "rejects", "mtbf", "faults"].map((k) => t(k)).join(", ");

/**
 * Sections of "KPIs of a line" (i18n `helpCenter.lineKpis.*`), in order, and
 * whether they have a formula (shown like the formulas of the machine KPIs).
 */
const LINE_KPI_SECTIONS: [string, boolean][] = [
  ["perMachine", false],
  ["positions", false],
  ["output", true],
  ["efficiency", true],
  ["downtime", true],
  ["rejects", true],
  ["mtbf", true],
  ["faults", true],
  ["targets", false],
];

/** Sections of "Live shift KPIs" (i18n `helpCenter.liveShiftKpis.*`), as LINE_KPI_SECTIONS. */
const LIVE_SHIFT_SECTIONS: [string, boolean][] = [
  ["targets", false],
  ["completion", true],
  ["expected", true],
  ["projected", true],
  ["needed", true],
  ["rejectRatio", true],
  ["colours", true],
  ["pace", true],
  ["score", true],
  ["lineOutput", true],
  ["lineDown", true],
  ["lineCause", true],
  ["lost", true],
];

/** Labels of the machine tabs as MachineView shows them (not translated there). */
const MACHINE_TABS = { overview: "Overview", preset: "Preset", liveShift: "Live Shift" };

/** Keys of a chart with a range bar (BaseChart), once the chart is clicked. */
export const chartKeys = (t: T): [string[], string][] => [
  [["←", "→"], t(`${C}.shortcuts.chartMove`)],
  [["↑"], t(`${C}.shortcuts.chartWiden`)],
  [["↓"], t(`${C}.shortcuts.chartNarrow`)],
  [["Esc"], t(`${C}.shortcuts.chartReset`)],
];

// ---------------------------------------------------------------- handbook

export type ArticleId =
  | "start"
  | "quickStart"
  | "findMachine"
  | "recentlyAll"
  | "navigation"
  | "machineStatus"
  | "analysisAreas"
  | "charts"
  | "timeRange"
  | "exportCharts"
  | "liveShift"
  | "compare"
  | "kpiPerformance"
  | "kpiProduction"
  | "kpiTime"
  | "kpiMtbf"
  | "lineKpis"
  | "liveShiftKpis"
  | "assistant"
  | "symbols"
  | "connectionIssues"
  | "dataRefresh"
  | "contact";

type Group = "basics" | "analysis" | "kpis" | "assistant" | "support";

const GROUP_KEYS: Record<Group, string> = {
  basics: `${C}.groups.basics`,
  analysis: `${C}.groups.analysis`,
  // "Calculation methods", the old page's tab
  kpis: `${P}.calculation.title`,
  assistant: `${C}.groups.assistant`,
  support: `${C}.groups.support`,
};
const GROUPS: Group[] = ["basics", "analysis", "kpis", "assistant", "support"];

/** The handbook groups in the active language, in display order. */
export const articleGroups = (t: T): string[] => GROUPS.map((g) => t(GROUP_KEYS[g]));

type Kpi =
  | "oee"
  | "efficiency"
  | "utilization"
  | "output"
  | "rejectRate"
  | "runtime"
  | "maxRuntime"
  | "downtime"
  | "plannedDowntime"
  | "externalDowntime"
  | "mtbf";

export interface CockpitArticle extends HelpArticle {
  id: ArticleId;
  tour?: TourId;
}

/** Every handbook article (the cockpit has no admin-only help). */
export function buildArticles(t: T): CockpitArticle[] {
  const group = (g: Group) => t(GROUP_KEYS[g]);
  const article = (
    id: ArticleId,
    g: Group,
    icon: IconName,
    a: { title: string; summary: string; sections: HelpArticleSection[]; tour?: TourId; keys?: [string[], string][] },
  ): CockpitArticle => ({ id, group: group(g), icon, ...a });

  const fs = `${P}.firstSteps`;
  const dv = `${P}.dataVisualization`;
  const faq = `${P}.faq`;
  const pairs = (base: string, ids: string[]): [string, string][] =>
    ids.map((id) => [t(`${base}.${id}.title`), t(`${base}.${id}.description`)]);
  const kpi = (k: Kpi): HelpArticleSection => ({
    h: t(`${P}.calculation.kpis.${k}.name`),
    p: [t(`${P}.calculation.kpis.${k}.description`), t(`${C}.kpi.${k}.insight`)],
    ul: [[t(`${C}.kpi.formula`), t(`${C}.kpi.${k}.formula`)]],
  });
  /** Sections shaped like the machine KPIs: name, description and, where there is one, the formula. */
  const formulaSections = (base: string, list: [string, boolean][]): HelpArticleSection[] =>
    list.map(([k, hasFormula]) => ({
      h: t(`${base}.${k}.name`),
      p: [t(`${base}.${k}.description`)],
      ...(hasFormula ? { ul: [[t(`${C}.kpi.formula`), t(`${base}.${k}.formula`)] as [string, string]] } : {}),
    }));
  const kpiArticle = (id: ArticleId, icon: IconName, kpis: Kpi[]) =>
    article(id, "kpis", icon, {
      title: t(`${C}.articles.${id}.title`),
      summary: t(`${C}.articles.${id}.summary`),
      sections: kpis.map(kpi),
    });

  return [
    // Getting started
    article("start", "basics", "home", {
      title: t(`${fs}.welcomeSection.title`),
      summary: t(`${fs}.welcomeSection.description`),
      sections: [
        { h: heading(t(`${fs}.welcomeSection.whatYouSee.title`)), ul: list(t, `${fs}.welcomeSection.whatYouSee.items`) },
      ],
      tour: "start",
    }),
    article("quickStart", "basics", "route", {
      title: t(`${P}.quickStart.title`),
      summary: t(`${C}.articles.quickStart.summary`),
      sections: ["checkMachineStatus", "analyzeMachineData", "findSpecificMachine", "compareShiftData"].map((goal) => ({
        h: t(`${P}.quickStart.${goal}.title`),
        ul: list(t, `${P}.quickStart.${goal}.steps`),
      })),
      tour: "start",
    }),
    article("findMachine", "basics", "search", {
      title: t(`${faq}.findMachine.question`),
      summary: t(`${C}.articles.findMachine.summary`),
      sections: [
        {
          p: [t(`${faq}.findMachine.answer`)],
          ul: list(t, `${faq}.findMachine.methods`),
          tip: tipText(t(`${faq}.findMachine.tip`)),
        },
      ],
      tour: "start",
    }),
    article("recentlyAll", "basics", "history", {
      title: t(`${fs}.recentlyAll.title`),
      summary: t(`${fs}.recentlyAll.description`),
      sections: [{ ul: pairs(`${fs}.recentlyAll`, ["recently", "all"]) }],
    }),
    article("navigation", "basics", "layers", {
      title: t(`${fs}.navigationMethods.title`),
      summary: sentence(t(`${fs}.navigationMethods.description`)),
      sections: [{ ul: pairs(`${fs}.navigationMethods`, ["treeView", "cardView", "globeView"]) }],
    }),
    article("machineStatus", "basics", "eye", {
      title: t(`${fs}.machineOverview.title`),
      summary: sentence(t(`${fs}.machineOverview.description`)),
      sections: [
        { ul: STATUS.map(([c, dot]) => marked(dot, listItem(t(`${fs}.machineOverview.statusColors.${c}`)))) },
      ],
    }),

    // Analysing machines
    article("analysisAreas", "analysis", "presentation", {
      title: t(`${fs}.analysisAreas.title`),
      summary: sentence(t(`${fs}.analysisAreas.description`)),
      sections: ["efficiency", "output", "downtime", "mtbf"].map((area) => ({
        h: t(`${fs}.analysisAreas.${area}.title`),
        p: [t(`${fs}.analysisAreas.${area}.description`)],
        ul: list(t, `${fs}.analysisAreas.${area}.items`),
      })),
      tour: "machine",
    }),
    article("charts", "analysis", "presentation", {
      title: t(`${dv}.chartsAndGraphs.title`),
      summary: sentence(t(`${dv}.chartsAndGraphs.description`)),
      sections: [
        { ul: pairs(`${dv}.chartsAndGraphs`, ["lineCharts", "barCharts", "pieCharts"]) },
        {
          h: t(`${dv}.interactiveFeatures.title`),
          p: [t(`${dv}.interactiveFeatures.description`)],
          ul: list(t, `${dv}.interactiveFeatures.features`),
        },
      ],
      keys: chartKeys(t),
      tour: "machine",
    }),
    article("timeRange", "analysis", "clock", {
      title: t(`${dv}.timeRangeSelection.title`),
      summary: sentence(t(`${dv}.timeRangeSelection.description`)),
      sections: [
        { h: heading(t(`${dv}.timeRangeSelection.presets.title`)), ul: list(t, `${dv}.timeRangeSelection.presets.items`) },
        {
          h: heading(t(`${dv}.timeRangeSelection.shiftFilter.title`)),
          p: [t(`${dv}.timeRangeSelection.shiftFilter.description`)],
        },
      ],
      tour: "machine",
    }),
    article("exportCharts", "analysis", "doc", {
      title: t(`${faq}.exportCharts.question`),
      summary: t(`${C}.articles.exportCharts.summary`),
      sections: [
        { p: [t(`${faq}.exportCharts.answer`)], ul: list(t, `${faq}.exportCharts.steps`) },
        { h: t(`${C}.articles.exportCharts.dataTitle`), p: [t(`${C}.articles.exportCharts.data`)] },
      ],
      tour: "machine",
    }),
    article("liveShift", "analysis", "bolt", {
      title: t("liveShift"),
      summary: t(`${C}.articles.liveShift.summary`),
      sections: [
        {
          p: [
            t(`${C}.articles.liveShift.p1`, { tab: MACHINE_TABS.liveShift }),
            t(`${C}.articles.liveShift.p2`, { overview: MACHINE_TABS.overview }),
            t(`${C}.articles.liveShift.p3`),
          ],
        },
      ],
      tour: "machine",
    }),
    article("compare", "analysis", "layers", {
      title: t("compareMachine"),
      summary: t(`${C}.articles.compare.summary`),
      sections: [
        {
          p: [
            t(`${C}.articles.compare.p1`, { page: t("compareMachine") }),
            t(`${C}.articles.compare.p2`, { kpis: kpiTabs(t) }),
          ],
        },
      ],
    }),

    // Calculation methods
    kpiArticle("kpiPerformance", "bolt", ["oee", "efficiency", "utilization"]),
    kpiArticle("kpiProduction", "layers", ["output", "rejectRate"]),
    kpiArticle("kpiTime", "clock", ["runtime", "maxRuntime", "downtime", "plannedDowntime", "externalDowntime"]),
    kpiArticle("kpiMtbf", "shield", ["mtbf"]),
    // KPIs of a line, written like the machine KPIs (the line tabs: components/line)
    article("lineKpis", "kpis", "route", {
      title: t(`${C}.articles.lineKpis.title`),
      summary: t(`${C}.articles.lineKpis.summary`),
      sections: formulaSections(`${C}.lineKpis`, LINE_KPI_SECTIONS),
    }),
    // the Live Shift tabs against the targets (components/live)
    article("liveShiftKpis", "kpis", "bolt", {
      title: t(`${C}.articles.liveShiftKpis.title`),
      summary: t(`${C}.articles.liveShiftKpis.summary`),
      sections: formulaSections(`${C}.liveShiftKpis`, LIVE_SHIFT_SECTIONS),
    }),

    // AI assistant
    article("assistant", "assistant", "sparkles", {
      title: t(`${C}.articles.assistant.title`),
      summary: t(`${C}.articles.assistant.summary`),
      sections: [
        { h: t(`${C}.articles.assistant.askTitle`), p: [t(`${C}.articles.assistant.ask`)] },
        { h: t(`${C}.articles.assistant.contextTitle`), p: [t(`${C}.articles.assistant.context`)] },
        { h: t(`${C}.articles.assistant.sourcesTitle`), p: [t(`${C}.articles.assistant.sources`)] },
        {
          h: t("cockpitSessions"),
          p: [t(`${C}.articles.assistant.history`, { page: t("cockpitSessions") })],
          tip: t(`${C}.articles.assistant.tip`),
        },
      ],
      tour: "assistant",
    }),

    // Help and contact
    article("symbols", "support", "info", {
      title: t(`${faq}.symbolsMeaning.question`),
      summary: t(`${C}.articles.symbols.summary`),
      sections: [
        {
          p: [t(`${faq}.symbolsMeaning.answer`)],
          ul: STATUS.map(([c, dot]) => marked(dot, listItem(t(`${faq}.symbolsMeaning.statusColors.${c}`)))),
        },
        { ul: SYMBOLS.map(([s, mark]) => marked(mark, listItem(t(`${faq}.symbolsMeaning.symbols.${s}`)))) },
      ],
    }),
    article("connectionIssues", "support", "alert", {
      title: t(`${faq}.connectionIssues.question`),
      summary: t(`${C}.articles.connectionIssues.summary`),
      sections: [{ p: [t(`${faq}.connectionIssues.answer`)], ul: list(t, `${faq}.connectionIssues.steps`) }],
    }),
    article("dataRefresh", "support", "refresh", {
      title: t(`${P}.dataRefresh.title`),
      summary: t(`${C}.articles.dataRefresh.summary`),
      sections: [
        {
          h: t(`${P}.dataRefresh.automaticRefresh.title`),
          p: [t(`${P}.dataRefresh.automaticRefresh.description`)],
          ul: list(t, `${P}.dataRefresh.automaticRefresh.features`),
        },
        {
          h: t(`${P}.dataRefresh.manualRefresh.title`),
          p: [t(`${P}.dataRefresh.manualRefresh.description`)],
          ul: list(t, `${P}.dataRefresh.manualRefresh.steps`),
        },
      ],
    }),
    article("contact", "support", "mail", {
      title: t(`${P}.contact.title`),
      summary: t(`${P}.contact.description`),
      sections: [
        {
          h: t(`${P}.contact.technicalSupport.title`),
          ul: [[t(`${C}.articles.contact.email`), t(`${P}.contact.technicalSupport.email`)]],
        },
        {
          h: t(`${P}.contact.servicePortal.title`),
          p: [t(`${P}.contact.servicePortal.description`), t(`${C}.articles.contact.portal`, { button: t("servicePortal") })],
        },
        {
          h: t(`${faq}.needMoreHelp.title`),
          p: [t(`${faq}.needMoreHelp.description`)],
          ul: list(t, `${faq}.needMoreHelp.options`),
        },
      ],
    }),
  ];
}

// ---------------------------------------------------------------- tours

/** A tour step and where the screen has to be for it (CockpitTourProvider's prepare). */
export interface CockpitTourStep extends BaseStep {
  route?: "home" | "machine";
  /** the KI-Assistent drawer for this step */
  assistant?: "open" | "closed";
  /** the machine tab the step shows (machines with live data open on Live Shift) */
  tab?: "overview";
}

export interface CockpitTour extends BaseTour<CockpitTourStep> {
  id: TourId;
  event: LearnEvent;
}

type StepOptions = Omit<CockpitTourStep, "id" | "title" | "body"> & { vars?: Record<string, string> };

function step(t: T, tour: TourId, id: string, { vars, ...o }: StepOptions = {}): CockpitTourStep {
  const k = `${C}.tours.${tour}.${id}`;
  return { id, title: t(`${k}.title`, vars ?? {}), body: [t(`${k}.body`, vars ?? {})], ...o };
}

/** The guided tours, in the order the sheet lists them. */
export function buildTours(t: T): CockpitTour[] {
  const tour = (id: TourId, icon: IconName, minutes: number, steps: CockpitTourStep[]): CockpitTour => ({
    id,
    event: `tour_${id}`,
    icon,
    minutes,
    title: t(`${C}.tours.${id}.title`),
    description: t(`${C}.tours.${id}.description`),
    steps,
  });
  const ranges = ["today", "lastWeek", "thisMonth"].map((k) => t(k)).join(", ");
  return [
    tour("start", "sparkles", 1, [
      step(t, "start", "welcome", { route: "home", icon: "sparkles" }),
      step(t, "start", "menu", { target: "menu-button", route: "home", placement: "bottom", icon: "menu" }),
      step(t, "start", "search", { target: "header-search", route: "home", placement: "bottom", icon: "search" }),
      {
        ...step(t, "start", "tabs", { target: "home-tabs", route: "home", placement: "bottom", icon: "home", vars: { home: t("home") } }),
        points: [
          [t("welcomeTab"), t(`${C}.tours.start.tabs.welcome`)],
          [t("recentlyViewed"), t(`${C}.tours.start.tabs.recently`)],
          [t("all"), t(`${C}.tours.start.tabs.all`)],
        ],
      },
      step(t, "start", "assistant", {
        target: "assistant-button",
        route: "home",
        assistant: "closed",
        placement: "left",
        icon: "sparkles",
        optional: true,
      }),
      {
        ...step(t, "start", "help", { target: "help-button", placement: "bottom", icon: "question" }),
        keys: [[["F1"], t(`${C}.shortcuts.help`)]],
      },
      step(t, "start", "user", { target: "user-menu", placement: "bottom", icon: "user" }),
    ]),
    tour("machine", "presentation", 2, [
      step(t, "machine", "header", { target: "machine-card", route: "machine", placement: "bottom", icon: "cpu" }),
      step(t, "machine", "tabs", {
        target: "machine-tabs",
        route: "machine",
        placement: "bottom",
        icon: "layers",
        vars: MACHINE_TABS,
      }),
      step(t, "machine", "range", {
        target: "date-range",
        route: "machine",
        tab: "overview",
        placement: "bottom",
        icon: "clock",
        vars: { ranges },
      }),
      step(t, "machine", "shifts", {
        target: "shift-selector",
        route: "machine",
        tab: "overview",
        placement: "bottom",
        icon: "clock",
        optional: true,
      }),
      step(t, "machine", "kpis", {
        target: "kpi-tabs",
        route: "machine",
        tab: "overview",
        placement: "bottom",
        icon: "presentation",
        vars: { kpis: kpiTabs(t) },
      }),
      step(t, "machine", "export", {
        target: "chart-export",
        route: "machine",
        tab: "overview",
        placement: "top",
        icon: "doc",
        optional: true,
      }),
      step(t, "machine", "liveShift", {
        target: "machine-tabs-live-shift",
        route: "machine",
        placement: "bottom",
        icon: "bolt",
        optional: true,
        vars: { tab: MACHINE_TABS.liveShift },
      }),
    ]),
    tour("assistant", "sparkles", 1, [
      step(t, "assistant", "launcher", {
        target: "assistant-button",
        route: "machine",
        assistant: "closed",
        placement: "left",
        icon: "sparkles",
      }),
      step(t, "assistant", "context", { target: "assistant-drawer", assistant: "open", placement: "left", icon: "info" }),
      {
        ...step(t, "assistant", "ask", {
          target: "assistant-composer",
          assistant: "open",
          placement: "left",
          icon: "message",
          interactive: true,
        }),
        tryIt: t(`${C}.tours.assistant.ask.tryIt`),
      },
      step(t, "assistant", "history", {
        target: "assistant-history",
        assistant: "open",
        placement: "left",
        icon: "history",
        optional: true,
        vars: { page: t("cockpitSessions") },
      }),
      {
        id: "sources",
        icon: "book",
        assistant: "open",
        title: t(`${C}.articles.assistant.sourcesTitle`),
        body: [t(`${C}.articles.assistant.sources`)],
      },
    ]),
  ];
}

// ---------------------------------------------------------------- learning path

/** The learning path: first steps, the everyday actions and the tours. */
export function buildTasks(t: T): LearnTask[] {
  const task = (
    event: LearnEvent,
    key: string,
    kind: TaskAction,
    label: string = t(`${C}.tasks.${key}.button`),
    vars: Record<string, string> = {},
  ): LearnTask => ({
    event,
    title: t(`${C}.tasks.${key}.title`, vars),
    how: t(`${C}.tasks.${key}.how`, vars),
    action: { kind, label },
  });
  const tourButton = t(`${C}.tasks.tourButton`);
  return [
    task("intro_seen", "intro", "intro"),
    task("tour_start", "tourStart", "tour:start", tourButton),
    task("machine_opened", "machine", "home", t("home")),
    task("tour_machine", "tourMachine", "tour:machine", tourButton),
    task("range_changed", "range", "machine"),
    task("chart_exported", "export", "machine"),
    task("help_read", "handbook", "article:kpiMtbf"),
    task("assistant_asked", "assistant", "assistant"),
    task("tour_assistant", "tourAssistant", "tour:assistant", tourButton),
    task("compare_used", "compare", "compare", undefined, { page: t("compareMachine") }),
  ];
}
